<template>
  <div class="sales-category-setting">
    <BobPageTitle :config="pageConfig" />

    <slot name="before-filters" />

    <slot name="filters" :filters="search" :refresh="refresh">
      <FiltersContainer :flat="tableConfig.filterFlat">
        <template v-for="filter in getFilters">
          <component
            :is="filter.component"
            :key="filter.key"
            v-model="search[filter.key]"
            v-bind="filter.props ? filter.props(search) : filter.props"
            v-on="filter.on ? filter.on(search) : filter.on"
            @clear="refresh(true)"
            @refresh="refresh(true)"
            @keypress.enter.native="refresh(true)"
            @change="refresh(true)"
          />
        </template>
      </FiltersContainer>
    </slot>

    <slot name="before-table" />

    <section>
      <BaseTable
        v-loading="loading.table"
        :data="displayTableData"
        v-bind="tableConfig.tableAttrs"
        v-on="tableListeners"
      >
        <EmptyBlock slot="empty" />
        <BaseElTableColumn
          v-for="column in columns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :align="column.align || 'center'"
          :fixed="column.fixed"
          :width="column.width"
          :min-width="column.minWidth"
          :sortable="column.sortable"
          v-bind="column.columnAttrs"
        >
          <template slot-scope="scope">
            <component
              :is="column.component || 'div'"
              v-bind="column.props ? column.props(scope.row) : column.props"
              v-on="column.on? column.on(scope.row) : column.on"
            >
              <div v-if="column.render">{{ column.render(scope.row) }}</div>
            </component>
          </template>
        </BaseElTableColumn>
      </BaseTable>

      <Pagination
        v-if="getPagination.enabled"
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="tableDataCount"
        @pageChange="refresh(false)"
      />
    </section>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onBeforeMount, set } from 'vue'
import TableEditBtnGroup from '@/components/Button/TableEditBtnGroup.vue'
import EmptyBlock from '@/components/EmptyBlock.vue'
import ThumbnailDefault from '@/assets/classTicket/thumbnail_default.svg'
import { get, forEach } from 'lodash'
import { useTable } from '@/use/table'
import { useRoute } from 'vue-router/composables'
import notifyMessage from '@/config/notifyMessage'
import BobPageTitle from './BobPageTitle.vue'

export default defineComponent({
  name: 'BobProPage',
  components: {
    EmptyBlock,
    TableEditBtnGroup,
    BobPageTitle,
  },
  props: {
    columns: {
      type: Array,
      default: () => [{
        prop: 'name',
        label: '名稱',
        component: 'BaseElTableColumn',
        render: (row) => row.name,
        props: () => ({
          className: 'break-word',
          width: 200,
        }),
      }],
    },
    tableConfig: {
      type: Object,
      default: () => ({
        pagination: {
          enabled: true,
          pageLimit: 10,
          pageSizes: [10, 20, 50],
        },
        apis: {
          get: async () => [],
          count: async () => 0,
          getPayload: () => ({}),
          delete: async () => {},
        },
        filters: [
          {
            key: 'name',
            type: 'input',
            label: '名稱',
            placeholder: '請輸入名稱',
            defaultValue: '',
          },
        ],
        tableDataPath: undefined,
      }),
    },
    pageConfig: {
      type: Object,
      default: () => ({
        title: '',
        actions: [],
      }),
    },
    // 为了向后兼容，保留config属性
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { emit }) {
    const {
      tableData,
      tableOptions,
      tableDataCount,
      loading,
      pageStartIndex,
      fetchData,
      fetchDataCount,
      shopId,
      dateFormat,
    } = useTable()
    const route = useRoute()
    const search = reactive({})
    const selectRow = ref(null)
    // 获取页面配置，优先使用pageConfig，兼容旧的config结构
    const displayPageData = computed(() => {
      return {
        title: props.pageConfig.title || (props.config && props.config.title) || '',
      }
    })

    // 获取表格过滤器，优先使用tableConfig，兼容旧的config结构
    const getFilters = computed(() => {
      return props.tableConfig.filters || (props.config && props.config.filters) || []
    })

    // 获取分页配置，优先使用tableConfig，兼容旧的config结构
    const getPagination = computed(() => {
      return props.tableConfig.pagination || (props.config && props.config.pagination) || { enabled: true, pageLimit: 10 }
    })

    // 获取API配置，优先使用tableConfig，兼容旧的config结构
    const getApis = computed(() => {
      return props.tableConfig.apis || (props.config && props.config.apis) || {
        get: async () => [],
        count: async () => 0,
        getPayload: () => ({}),
        delete: async () => {},
      }
    })

    // Sort state
    const sortConfig = computed(() => props.tableConfig.sortConfig || { enabled: false })
    const currentSort = reactive({ prop: null, order: null })

    const orderMap = { ascending: 'ASC', descending: 'DESC' }

    const onSortChange = ({ prop, order }) => {
      if (!sortConfig.value.enabled) return
      currentSort.prop = prop || null
      currentSort.order = order || null
      refresh(true)
    }

    const tableListeners = computed(() => {
      const listeners = { ...(props.tableConfig.tableOn || {}) }
      if (sortConfig.value.enabled) {
        listeners['sort-change'] = onSortChange
      }
      return listeners
    })

    const displayTableData = computed(() => {
      // return map(tableData.value, (item) => item)
      return tableData.value
    })

    const getTableData = async () => {
      const payload = {
        shopId: shopId.value,
        start: pageStartIndex.value,
        limit: tableOptions.pageLimit,
      }

      // 使用getPayload方法获取自定义的payload
      const customPayload = getApis.value.getPayload ? getApis.value.getPayload(search) : {}
      const finalPayload = { ...payload, ...customPayload }

      // Inject sort params if sortConfig is enabled
      if (sortConfig.value.enabled && currentSort.prop && currentSort.order) {
        finalPayload.sort = currentSort.prop
        finalPayload.sortBy = orderMap[currentSort.order]
      }

      await Promise.allSettled([
        fetchData(getApis.value.get, finalPayload, props.tableConfig.tableDataPath),
        fetchDataCount(getApis.value.count, finalPayload),
      ])
    }

    const refresh = async (reset = false) => {
      if (loading.table) return
      if (reset) tableOptions.page = 1
      loading.table = true
      try {
        await getTableData()
      } finally {
        loading.table = false
      }
    }

    const deleteItem = async () => {
      try {
        await getApis.value.delete({
          shopId: shopId.value,
          id: get(selectRow, 'value.id'),
        })
        await refresh()
        window.$message.success(notifyMessage.deleteSuccess)
      } catch (error) {
        window.$message.error(error)
      }
    }

    onBeforeMount(async () => {
      tableOptions.pageLimit = getPagination.value.pageLimit || 10

      // init filters data
      forEach(getFilters.value, (filter) => {
        set(search, filter.key, get(filter, 'defaultValue'))
      })

      if (get(route.query, 'name')) {
        search.name = get(route.query, 'name')
      }
      await refresh()
    })

    return {
      tableData,
      tableDataCount,
      tableOptions,
      loading,
      search,
      deleteItem,
      selectRow,
      dateFormat,
      ThumbnailDefault,
      refresh,
      displayPageData,
      displayTableData,
      getFilters,
      getPagination,
      getApis,
      tableListeners,
    }
  },
})
</script>

<style scoped lang="scss">
.break-word {
  word-break: break-word;
}
</style>
